/**
 * Bundle Builder - Interactive Selection & Cart Management
 *
 * Features:
 * - Per-step selection (single/multiple modes)
 * - Visual state management
 * - Cart integration via Shopify Cart API
 * - Validation & error handling
 * - Accessibility support
 */

class BundleBuilderElement extends HTMLElement {
  constructor() {
    super();

    // State: Map<stepNumber, Set<variantId>> for horizontal layouts
    this.selections = new Map();

    // State: Map<variantId, quantity> for vertical layouts
    this.quantities = new Map();

    // Initialize selections for each step
    this.steps = this.querySelectorAll('.bundle-builder__step');
    this.steps.forEach((step) => {
      const stepNum = step.dataset.step;
      this.selections.set(stepNum, new Set());
    });

    this.isSubmitting = false;

    // Step gating config
    this.stepGatingEnabled = this.dataset.stepGating === 'true';
    this.gatingUnlockAfter = parseInt(this.dataset.gatingUnlockAfter) || 3;
  }

  connectedCallback() {
    this.attachEventListeners();
    this.setupCartIntegration();
    this.initializeScrollIndicators();
    this.setupProductModal();
    this.fixLastStepConnector();

    // Dispatch initial state to quantity meter
    this.dispatchQuantityChangeEvent();

    // Initialize step gating state
    if (this.stepGatingEnabled) {
      this.updateStepGating();
    }
  }

  /**
   * Check if prerequisite steps are completed and unlock/lock gated steps.
   * Progressive unlocking: each gated step requires ALL previous steps to be completed.
   * e.g. step 4 unlocks when 1+2+3 done, step 5 unlocks when 1+2+3+4 done.
   */
  updateStepGating() {
    if (!this.stepGatingEnabled) return;

    const completedSteps = this.getCompletedSteps();

    // Update each gated step individually
    const gatedSteps = this.querySelectorAll('[data-gated="true"]');
    gatedSteps.forEach((step) => {
      const stepNum = parseInt(step.dataset.step);
      const content = step.querySelector('.bundle-builder__step-content');
      const lockedMessage = step.querySelector('[data-locked-message]');

      // This step requires ALL steps before it (1 to stepNum-1) to be completed
      let allPreviousCompleted = true;
      for (let i = 1; i < stepNum; i++) {
        const prevStepEl = this.querySelector(`[data-step="${i}"]`);
        if (prevStepEl && !completedSteps.includes(String(i))) {
          allPreviousCompleted = false;
          break;
        }
      }

      if (allPreviousCompleted) {
        // Unlock
        if (step.classList.contains('bundle-builder__step--locked')) {
          step.classList.remove('bundle-builder__step--locked');
          step.classList.add('bundle-builder__step--unlocking');
          if (lockedMessage) lockedMessage.style.display = 'none';
          if (content) {
            content.style.display = '';
            // Re-check scroll indicators for newly visible content
            const scrollWrapper = content.querySelector('.bundle-builder__scroll-wrapper');
            if (scrollWrapper) {
              this.initializeScrollIndicators();
            }
          }
          // Remove animation class after it completes
          setTimeout(() => {
            step.classList.remove('bundle-builder__step--unlocking');
            // Update last step connector maxHeight to prevent overflow
            this.fixLastStepConnector();
          }, 600);
        }
      } else {
        // Lock
        if (!step.classList.contains('bundle-builder__step--locked')) {
          step.classList.add('bundle-builder__step--locked');
          if (lockedMessage) lockedMessage.style.display = '';
          if (content) content.style.display = 'none';
        }
      }
    });
  }

  /**
   * Fix last step connector height to not overflow
   */
  fixLastStepConnector() {
    const allSteps = this.querySelectorAll('.bundle-builder__step');
    if (allSteps.length === 0) return;

    const lastStep = allSteps[allSteps.length - 1];
    const connector = lastStep.querySelector('.bundle-builder__step-connector');

    if (connector && lastStep) {
      // Set max-height to parent's offsetHeight to prevent overflow
      const setConnectorHeight = () => {
        connector.style.maxHeight = lastStep.offsetHeight + 'px';
      };

      // Set initial height
      setTimeout(setConnectorHeight, 100);

      // Update on window resize
      window.addEventListener('resize', setConnectorHeight);
    }
  }

  /**
   * Get total quantity across all selections (horizontal + vertical)
   * @returns {number} Total quantity
   */
  getTotalQuantity() {
    let total = 0;

    // Count from horizontal/full layouts (each selection = 1 item)
    this.selections.forEach((selection) => {
      total += selection.size;
    });

    // Count from vertical layouts (quantities map)
    this.quantities.forEach((quantity) => {
      total += quantity;
    });

    return total;
  }

  /**
   * Get completed steps
   * @returns {Array<string>} Array of completed step numbers
   */
  getCompletedSteps() {
    const completed = [];

    this.steps.forEach((step) => {
      const stepNum = step.dataset.step;
      const isVerticalLayout = step.querySelector('.bundle-builder__products--vertical');

      if (isVerticalLayout) {
        // Check quantities for vertical layouts
        const cardsInStep = step.querySelectorAll('.bundle-product-card');
        let hasSelection = false;

        cardsInStep.forEach((card) => {
          const variantId = card.dataset.variantId;
          if (this.quantities.has(variantId) && this.quantities.get(variantId) > 0) {
            hasSelection = true;
          }
        });

        if (hasSelection) {
          completed.push(stepNum);
        }
      } else {
        // Check selections for horizontal layouts
        const selection = this.selections.get(stepNum);
        if (selection && selection.size > 0) {
          completed.push(stepNum);
        }
      }
    });

    return completed;
  }

  /**
   * Dispatch event to notify quantity meter of changes
   * Event: 'bundleBuilderQuantityChanged'
   */
  dispatchQuantityChangeEvent() {
    const totalQuantity = this.getTotalQuantity();
    const totalPrice = this.calculateTotalPrice();
    const completedSteps = this.getCompletedSteps();

    // Check if any selected product has etat_stock="precommande"
    const hasPreorder = this.hasPreorderSelected();

    document.dispatchEvent(new CustomEvent('bundleBuilderQuantityChanged', {
      detail: {
        totalQuantity: totalQuantity,
        totalPrice: totalPrice,
        selections: this.getSelectedItems(),
        completedSteps: completedSteps,
        hasPreorder: hasPreorder
      }
    }));

    // Update step gating after each selection change
    if (this.stepGatingEnabled) {
      this.updateStepGating();
    }
  }

  /**
   * Check if any currently selected product has data-etat-stock="precommande"
   * @returns {boolean}
   */
  hasPreorderSelected() {
    const selectedCards = this.querySelectorAll('.bundle-product-card--selected');
    for (const card of selectedCards) {
      if (card.dataset.etatStock === 'precommande') return true;
    }

    // Also check vertical layouts via quantities
    for (const [variantId, qty] of this.quantities) {
      if (qty > 0) {
        const card = this.querySelector(`.bundle-product-card[data-variant-id="${variantId}"]`);
        if (card && card.dataset.etatStock === 'precommande') return true;
      }
    }

    return false;
  }

  /**
   * Attach click/keyboard events to product cards
   */
  attachEventListeners() {
    // Event delegation for performance
    this.addEventListener('click', (e) => {
      const stepContainer = e.target.closest('.bundle-builder__step');
      const isVerticalLayout = stepContainer?.querySelector('.bundle-builder__products--vertical');

      // Block interactions on sold-out cards (except info button)
      const soldOutCard = e.target.closest('.bundle-product-card--sold-out');
      if (soldOutCard && !e.target.closest('.bundle-product-card__info-btn')) return;

      // Handle add button click (vertical mode)
      if (e.target.classList.contains('bundle-product-card__add-btn')) {
        e.stopPropagation();
        if (isVerticalLayout) {
          this.handleAddButtonClick(e.target);
        } else {
          const card = e.target.closest('.bundle-product-card');
          if (card) {
            this.handleProductClick(card);
          }
        }
        return;
      }

      // Handle quantity buttons (vertical mode)
      if (e.target.classList.contains('bundle-product-card__quantity-button')) {
        e.stopPropagation();
        this.handleQuantityButtonClick(e.target);
        return;
      }

      // Handle info button click (open modal)
      if (e.target.closest('.bundle-product-card__info-btn')) {
        e.stopPropagation();
        this.handleInfoButtonClick(e.target.closest('.bundle-product-card__info-btn'));
        return;
      }

      // Handle card click (non-vertical layouts only)
      if (!isVerticalLayout) {
        const card = e.target.closest('.bundle-product-card');
        if (card) {
          this.handleProductClick(card);
        }
      }
    });

    // Keyboard accessibility (only for non-vertical layouts)
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        const stepContainer = e.target.closest('.bundle-builder__step');
        const isVerticalLayout = stepContainer?.querySelector('.bundle-builder__products--vertical');

        if (!isVerticalLayout) {
          const card = e.target.closest('.bundle-product-card');
          if (card && !card.dataset.soldOut) {
            e.preventDefault();
            this.handleProductClick(card);
          }
        }
      }
    });
  }

  /**
   * Handle "Ajouter" button click in vertical mode
   */
  handleAddButtonClick(button) {
    const variantId = button.dataset.variantId;

    if (!variantId) return;

    const card = button.closest('.bundle-product-card');
    const quantityContainer = card.querySelector(`.bundle-product-card__quantity-controls[data-variant-id="${variantId}"]`);
    const quantityInput = quantityContainer?.querySelector('.bundle-product-card__quantity-input');

    if (!quantityContainer) return;

    // Hide "Ajouter" button, show quantity controls
    button.style.display = 'none';
    quantityContainer.style.display = 'flex';

    // Set initial quantity to 1
    this.quantities.set(variantId, 1);
    quantityInput.value = '1';

    // Update step completion
    this.updateStepCompletionForVertical(card);

    // Update selected color label (steps 1-3 only)
    const addStepNum = card.closest('.bundle-builder__step')?.dataset.step;
    if (addStepNum) this.updateSelectedColorLabel(addStepNum);

    // Update button price
    this.updateButtonPrice();

    // Dispatch event to quantity meter
    this.dispatchQuantityChangeEvent();
  }

  /**
   * Handle quantity button (+/-) click in vertical mode
   */
  handleQuantityButtonClick(button) {
    const action = button.name; // "plus" or "minus"
    const quantityControls = button.closest('.bundle-product-card__quantity-controls');
    const variantId = quantityControls.dataset.variantId;
    const quantityInput = quantityControls.querySelector('.bundle-product-card__quantity-input');
    const addButton = quantityControls.previousElementSibling;

    let currentQuantity = this.quantities.get(variantId) || 0;

    if (action === 'plus') {
      currentQuantity++;
    } else if (action === 'minus') {
      currentQuantity--;
    }

    // If quantity reaches 0, hide quantity controls and show "Ajouter" button
    if (currentQuantity <= 0) {
      this.quantities.delete(variantId);
      quantityControls.style.display = 'none';
      addButton.style.display = 'inline-flex';
      quantityInput.value = '0';
    } else {
      this.quantities.set(variantId, currentQuantity);
      quantityInput.value = currentQuantity.toString();
    }

    // Update step completion
    const card = button.closest('.bundle-product-card');
    this.updateStepCompletionForVertical(card);

    // Update selected color label (steps 1-3 only)
    const qtyStepNum = card.closest('.bundle-builder__step')?.dataset.step;
    if (qtyStepNum) this.updateSelectedColorLabel(qtyStepNum);

    // Update button price
    this.updateButtonPrice();

    // Dispatch event to quantity meter
    this.dispatchQuantityChangeEvent();
  }

  /**
   * Update step completion status for vertical layouts
   */
  updateStepCompletionForVertical(card) {
    const stepContainer = card.closest('.bundle-builder__step');
    const stepNum = stepContainer.dataset.step;

    // Check if any products in this step have quantity > 0
    const cardsInStep = stepContainer.querySelectorAll('.bundle-product-card');
    let hasSelection = false;

    cardsInStep.forEach((c) => {
      const variantId = c.dataset.variantId;
      if (this.quantities.has(variantId) && this.quantities.get(variantId) > 0) {
        hasSelection = true;
      }
    });

    // Update step completion visual state
    if (hasSelection) {
      stepContainer.classList.add('bundle-builder__step--completed');
      this.updateStepNumberIcon(stepNum, true);
      this.updateStepConnectorForVertical(stepNum);
    } else {
      stepContainer.classList.remove('bundle-builder__step--completed');
      this.updateStepNumberIcon(stepNum, false);
      this.updateStepConnectorForVertical(stepNum);
    }
  }

  /**
   * Update connector for vertical layouts
   */
  updateStepConnectorForVertical(stepNum) {
    const currentStep = this.querySelector(`[data-step="${stepNum}"]`);
    if (!currentStep) return;

    const connector = currentStep.querySelector('.bundle-builder__step-connector');
    if (!connector) return;

    const cardsInStep = currentStep.querySelectorAll('.bundle-product-card');
    let hasSelection = false;

    cardsInStep.forEach((card) => {
      const variantId = card.dataset.variantId;
      if (this.quantities.has(variantId) && this.quantities.get(variantId) > 0) {
        hasSelection = true;
      }
    });

    if (hasSelection) {
      connector.classList.add('bundle-builder__step-connector--completed');
    } else {
      connector.classList.remove('bundle-builder__step-connector--completed');
    }
  }

  /**
   * Handle product card click/selection
   */
  handleProductClick(card) {
    const stepContainer = card.closest('.bundle-builder__step');
    const stepNum = stepContainer.dataset.step;
    const selectionMode = stepContainer.dataset.selectionMode || 'single';
    const variantId = card.dataset.variantId;

    if (!variantId) return;

    const currentSelection = this.selections.get(stepNum);
    const isLastStep = this.isLastStep(stepNum);

    if (selectionMode === 'single') {
      // Single selection: can only switch to another option, not deselect
      if (currentSelection.has(variantId)) {
        // Already selected - do nothing (cannot deselect)
        return;
      } else {
        // Select this one, deselect others
        currentSelection.clear();
        currentSelection.add(variantId);
        this.updateStepVisualState(stepNum);

        // Update button price
        this.updateButtonPrice();

        // Dispatch event to quantity meter
        this.dispatchQuantityChangeEvent();
      }
    } else {
      // Multiple selection: can toggle, but cannot remove last item (or any item on last step)
      if (currentSelection.has(variantId)) {
        // On last step, don't allow deselection at all
        if (isLastStep) {
          return;
        }
        // Only allow deselect if there are other items selected
        if (currentSelection.size > 1) {
          currentSelection.delete(variantId);
          this.updateCardVisualState(card, false);
        } else {
          // Last item selected - cannot deselect
          return;
        }
      } else {
        currentSelection.add(variantId);
        this.updateCardVisualState(card, true);
      }

      // Update step completion status and connector
      const stepContainer = card.closest('.bundle-builder__step');
      if (currentSelection.size > 0) {
        stepContainer.classList.add('bundle-builder__step--completed');
        this.updateStepNumberIcon(stepNum, true);
      } else {
        stepContainer.classList.remove('bundle-builder__step--completed');
        this.updateStepNumberIcon(stepNum, false);
      }
      this.updateStepConnector(stepNum);

      // Update button price
      this.updateButtonPrice();

      // Dispatch event to quantity meter
      this.dispatchQuantityChangeEvent();
    }
  }

  /**
   * Check if a step is the last step
   */
  isLastStep(stepNum) {
    const allStepNums = Array.from(this.selections.keys());
    const maxStepNum = Math.max(...allStepNums.map(num => parseInt(num)));
    return parseInt(stepNum) === maxStepNum;
  }

  /**
   * Update visual state for all cards in a step (single mode)
   */
  updateStepVisualState(stepNum) {
    const stepContainer = this.querySelector(`[data-step="${stepNum}"]`);
    const cards = stepContainer.querySelectorAll('.bundle-product-card');
    const currentSelection = this.selections.get(stepNum);
    const selectionMode = stepContainer.dataset.selectionMode || 'single';

    // Add class for single mode opacity styling
    if (selectionMode === 'single') {
      stepContainer.classList.add('bundle-builder__step--single-mode');
    } else {
      stepContainer.classList.remove('bundle-builder__step--single-mode');
    }

    cards.forEach((card) => {
      const variantId = card.dataset.variantId;
      const isSelected = currentSelection.has(variantId);
      this.updateCardVisualState(card, isSelected);
    });

    // Mark step as completed if at least one product is selected
    if (currentSelection.size > 0) {
      stepContainer.classList.add('bundle-builder__step--completed');
      this.updateStepNumberIcon(stepNum, true);
    } else {
      stepContainer.classList.remove('bundle-builder__step--completed');
      this.updateStepNumberIcon(stepNum, false);
    }

    // Update connector line to next step
    this.updateStepConnector(stepNum);

    // Update selected color label (steps 1-3 only)
    this.updateSelectedColorLabel(stepNum);
  }

  /**
   * Update visual state for a single card
   */
  updateCardVisualState(card, isSelected) {
    if (isSelected) {
      card.classList.add('bundle-product-card--selected');
      card.setAttribute('aria-pressed', 'true');
    } else {
      card.classList.remove('bundle-product-card--selected');
      card.setAttribute('aria-pressed', 'false');
    }
  }

  /**
   * Update connector line between current step and next step
   */
  updateStepConnector(stepNum) {
    const currentStep = this.querySelector(`[data-step="${stepNum}"]`);
    const currentSelection = this.selections.get(stepNum);

    if (!currentStep) return;

    // Find the connector line after this step
    const connector = currentStep.querySelector('.bundle-builder__step-connector');

    if (connector) {
      if (currentSelection.size > 0) {
        connector.classList.add('bundle-builder__step-connector--completed');
      } else {
        connector.classList.remove('bundle-builder__step-connector--completed');
      }
    }
  }

  /**
   * Update selected color label in step title (steps 1-3 only)
   */
  updateSelectedColorLabel(stepNum) {
    if (parseInt(stepNum) > 3) return;

    const colorSpan = this.querySelector(`[data-step-color="${stepNum}"]`);
    if (!colorSpan) return;

    const colorValueSpan = colorSpan.querySelector('[data-color-value]');
    const stepContainer = this.querySelector(`[data-step="${stepNum}"]`);
    if (!stepContainer || !colorValueSpan) return;

    // Find the selected card's couleur (first selected card with a couleur)
    let couleur = null;
    const isVerticalLayout = stepContainer.querySelector('.bundle-builder__products--vertical');

    if (isVerticalLayout) {
      // Vertical: find first card with quantity > 0
      const cards = stepContainer.querySelectorAll('.bundle-product-card');
      cards.forEach((card) => {
        if (!couleur && card.dataset.couleur) {
          const variantId = card.dataset.variantId;
          if (this.quantities.has(variantId) && this.quantities.get(variantId) > 0) {
            couleur = card.dataset.couleur;
          }
        }
      });
    } else {
      // Horizontal: find first selected card
      const selection = this.selections.get(stepNum);
      if (selection) {
        selection.forEach((variantId) => {
          if (!couleur) {
            const card = stepContainer.querySelector(`[data-variant-id="${variantId}"]`);
            if (card && card.dataset.couleur) {
              couleur = card.dataset.couleur;
            }
          }
        });
      }
    }

    if (couleur) {
      colorValueSpan.textContent = couleur;
      colorSpan.style.display = 'inline';
    } else {
      colorSpan.style.display = 'none';
    }
  }

  /**
   * Update step number icon (show checkmark when completed)
   */
  updateStepNumberIcon(stepNum, isCompleted) {
    const stepContainer = this.querySelector(`[data-step="${stepNum}"]`);
    if (!stepContainer) return;

    const stepNumberElement = stepContainer.querySelector('.bundle-builder__step-number');
    if (!stepNumberElement) return;

    // Check if the step was already completed (to avoid re-triggering animation)
    const wasCompleted = stepNumberElement.classList.contains('bundle-builder__step-number--completed');

    if (isCompleted) {
      // Only trigger animation if transitioning from incomplete to complete
      if (!wasCompleted) {
        // Replace number with checkmark SVG
        stepNumberElement.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24">
            <path fill="#fff" d="m9.55 15.15l8.475-8.475q.3-.3.7-.3t.7.3t.3.713t-.3.712l-9.175 9.2q-.3.3-.7.3t-.7-.3L4.55 13q-.3-.3-.288-.712t.313-.713t.713-.3t.712.3z"/>
          </svg>
        `;
        stepNumberElement.classList.add('bundle-builder__step-number--completed');
      }
    } else {
      stepNumberElement.textContent = stepNum;
      stepNumberElement.classList.remove('bundle-builder__step-number--completed');
    }
  }

  /**
   * Validate all steps have at least one selection
   * Returns { valid: boolean, incompleteSteps: number[] }
   */
  validateAllSteps() {
    const incompleteSteps = [];

    this.steps.forEach((step) => {
      const stepNum = step.dataset.step;
      const isRequired = step.dataset.required !== 'false';

      // Skip validation for optional steps
      if (!isRequired) return;

      const isVerticalLayout = step.querySelector('.bundle-builder__products--vertical');

      if (isVerticalLayout) {
        // For vertical layouts, check quantities map
        const cardsInStep = step.querySelectorAll('.bundle-product-card');
        let hasSelection = false;

        cardsInStep.forEach((card) => {
          const variantId = card.dataset.variantId;
          if (this.quantities.has(variantId) && this.quantities.get(variantId) > 0) {
            hasSelection = true;
          }
        });

        if (!hasSelection) {
          incompleteSteps.push(stepNum);
        }
      } else {
        // For horizontal/full layouts, check selections map
        const selection = this.selections.get(stepNum);
        if (!selection || selection.size === 0) {
          incompleteSteps.push(stepNum);
        }
      }
    });

    return {
      valid: incompleteSteps.length === 0,
      incompleteSteps
    };
  }

  /**
   * Get all selected items as array of variant IDs
   */
  getSelectedItems() {
    const items = [];

    // Add items from horizontal/full layouts (selections map)
    this.selections.forEach((selection) => {
      selection.forEach((variantId) => {
        items.push({
          id: variantId,
          quantity: 1
        });
      });
    });

    // Add items from vertical layouts (quantities map)
    this.quantities.forEach((quantity, variantId) => {
      if (quantity > 0) {
        items.push({
          id: variantId,
          quantity: quantity
        });
      }
    });

    return items;
  }

  /**
   * Calculate total price of all selected items
   * @returns {number} Total price
   */
  calculateTotalPrice() {
    let total = 0;

    // Calculate from horizontal/full layouts (selections map)
    this.selections.forEach((selection) => {
      selection.forEach((variantId) => {
        const card = this.querySelector(`[data-variant-id="${variantId}"]`);
        if (card) {
          const price = parseFloat(card.dataset.price) || 0;
          total += price;
        }
      });
    });

    // Calculate from vertical layouts (quantities map)
    this.quantities.forEach((quantity, variantId) => {
      if (quantity > 0) {
        const card = this.querySelector(`[data-variant-id="${variantId}"]`);
        if (card) {
          const price = parseFloat(card.dataset.price) || 0;
          total += price * quantity;
        }
      }
    });

    return total;
  }

  /**
   * Update button price display
   * Dispatches event to button-price-manager
   */
  updateButtonPrice() {
    const total = this.calculateTotalPrice();

    // Dispatch event for button-price-manager to listen
    document.dispatchEvent(new CustomEvent('updateButtonPrice', {
      detail: {
        total: total,
        currency: 'EUR',
        locale: 'fr-FR'
      }
    }));
  }

  /**
   * Setup cart integration with main product form
   */
  setupCartIntegration() {
    // Find the main product form - try multiple strategies
    let productForm = document.querySelector('product-form form[action*="/cart/add"]');

    if (!productForm) {
      productForm = document.querySelector('product-form form');
    }

    if (!productForm) {
      const sectionId = this.closest('[data-section-id]')?.dataset?.sectionId;
      if (sectionId) {
        const formId = 'product-form-' + sectionId;
        productForm = document.getElementById(formId);
      }
    }

    if (!productForm) {
      return;
    }


    // Intercept form submission with capture: true to run BEFORE other listeners
    productForm.addEventListener('submit', async (e) => {

      if (this.isSubmitting) {
        return;
      }

      // CRITICAL: Prevent default submission
      e.preventDefault();
      e.stopPropagation();

      await this.addBundleToCart();
    }, { capture: true });
  }

  /**
   * Add bundle items to cart via Shopify Cart API
   */
  async addBundleToCart() {
    // Validate selections
    const validation = this.validateAllSteps();

    if (!validation.valid) {
      this.showValidationError(validation.incompleteSteps);
      return;
    }

    if (this.isSubmitting) {
      return;
    }

    this.isSubmitting = true;

    // Show loading state
    this.showLoadingState();

    // Get selected items
    const items = this.getSelectedItems();

    if (items.length === 0) {
      this.hideLoadingState();
      this.isSubmitting = false;
      return;
    }

    // Reverse items order so step 1 is added last (appears at bottom of cart)
    items.reverse();

    // Prepare cart data
    const cartData = {
      items: items.map(item => ({
        id: item.id,
        quantity: item.quantity
      }))
    };

    // Get cart drawer element
    const cartDrawer = document.querySelector('drawer-cart');
    const submitButton = document.querySelector('product-form button[type="submit"]');

    // Add bundle items to cart
    HELPER_UTIL.fetchData({
      method: 'POST',
      config: {
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
          'Content-Type': 'application/json'
        },
      },
      url: `${window.Shopify.routes.root}cart/add.js?sections=${HELPER_UTIL.sectionRenderUtil('cart-drawer').getSectionsString()}`,
      data: JSON.stringify(cartData),
      onsuccess: (data) => {
        // Hide loading state
        this.hideLoadingState();

        // Trigger cart change event to update cart drawer (same as normal product add)
        if (cartDrawer) {
          window.currentActiveElement = submitButton;
          HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerCartChange,
            submitButton || this,
            { sections: data.sections }
          );
          // Show button Complete state
          if (submitButton) {
            HELPER_UTIL.submitButtonState(submitButton).completeState();
          }
        } else {
          window.location.href = `${window.Shopify.routes.root}cart`;
        }

        this.isSubmitting = false;
      },
      onerror: (error) => {
        this.hideLoadingState();
        this.isSubmitting = false;

        if (error.json) {
          error.json().then((errorObj) => {
            if (errorObj.status === 422) {
              HELPER_UTIL.dispatchCustomEvent(
                _EVENT_HELPER.triggerToastMessage,
                document,
                {
                  toastType: 'common-errors',
                  messageType: 'error',
                  message: errorObj.description,
                }
              );
            }
          });
        } else {
          HELPER_UTIL.dispatchCustomEvent(
            _EVENT_HELPER.triggerToastMessage,
            document,
            {
              toastType: 'common-errors',
              messageType: 'error',
              message: 'Une erreur est survenue. Réessaie dans quelques instants.',
            }
          );
        }
      },
      defaultCb: () => {
        setTimeout(() => {
          if (submitButton) {
            HELPER_UTIL.submitButtonState(submitButton).resetState(1000);
            HELPER_UTIL.submitButtonState(submitButton).disableState(false);
          }
        }, 1000);
      },
    });
  }

  /**
   * Show validation error for incomplete steps
   */
  showValidationError(incompleteSteps) {
    // Mark incomplete steps visually
    incompleteSteps.forEach((stepNum) => {
      const step = this.querySelector(`[data-step="${stepNum}"]`);
      step.classList.add('bundle-builder__step--incomplete');

      // Remove class after animation
      setTimeout(() => {
        step.classList.remove('bundle-builder__step--incomplete');
      }, 2000);
    });

    // Scroll to first incomplete step
    if (incompleteSteps.length > 0) {
      const firstIncomplete = this.querySelector(`[data-step="${incompleteSteps[0]}"]`);
      firstIncomplete.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    this.showError(`Sélectionne un produit pour l'étape${incompleteSteps.length > 1 ? 's' : ''} ${incompleteSteps.join(', ')}`);
  }

  /**
   * Show generic error message
   */
  showError(message) {
    HELPER_UTIL.dispatchCustomEvent(
      _EVENT_HELPER.triggerToastMessage,
      document,
      {
        toastType: 'common-errors',
        messageType: 'error',
        message: message,
      }
    );
  }

  /**
   * Show loading state on submit button
   */
  showLoadingState() {
    const submitBtn = document.querySelector('product-form button[type="submit"]');
    if (submitBtn) {
      HELPER_UTIL.submitButtonState(submitBtn).loadingState();
    }
  }

  /**
   * Hide loading state
   */
  hideLoadingState() {
    const submitBtn = document.querySelector('product-form button[type="submit"]');
    if (submitBtn) {
      HELPER_UTIL.submitButtonState(submitBtn).resetState();
    }
  }

  /**
   * Handle info button click to open modal
   */
  handleInfoButtonClick(button) {
    const productHandle = button.dataset.productHandle;
    const card = button.closest('.bundle-product-card');
    const variantId = card?.dataset.variantId;
    const restockDate = card?.dataset.restockDate || null;

    if (!productHandle) {
      console.warn('No product handle found for info button');
      return;
    }

    // Store current variant ID for qty picker
    this.currentModalVariantId = variantId;

    // Show/hide restock notice in modal
    this.updateModalRestockNotice(restockDate);

    // Open modal and fetch quick view content
    this.openProductModal(productHandle, variantId);
  }

  /**
   * Show or hide the restock notice in the product modal
   * @param {string|null} restockDate - Restock date string or null
   */
  updateModalRestockNotice(restockDate) {
    const restockEl = document.getElementById('bundle-modal-restock');
    const restockTextEl = document.getElementById('bundle-modal-restock-text');
    if (!restockEl || !restockTextEl) return;

    if (restockDate) {
      restockTextEl.innerHTML = `En rupture, de retour en stock le <strong>${restockDate}</strong>`;
      restockEl.style.display = 'flex';
    } else {
      restockEl.style.display = 'none';
      restockTextEl.innerHTML = '';
    }
  }

  /**
   * Setup product info modal
   */
  setupProductModal() {
    const modal = document.getElementById('bundle-product-modal');
    if (!modal) return;

    const closeButton = modal.querySelector('.bundle-product-modal__close');
    const overlay = modal.querySelector('.bundle-product-modal__overlay');

    // Modal qty picker elements
    const qtyMinus = document.getElementById('bundle-modal-qty-minus');
    const qtyPlus = document.getElementById('bundle-modal-qty-plus');

    // Close modal on close button click
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeProductModal();
      });
    }

    // Close modal on overlay click
    if (overlay) {
      overlay.addEventListener('click', () => {
        this.closeProductModal();
      });
    }

    // Qty picker: minus button
    if (qtyMinus) {
      qtyMinus.addEventListener('click', () => {
        this.handleModalQtyChange('minus');
      });
    }

    // Qty picker: plus button
    if (qtyPlus) {
      qtyPlus.addEventListener('click', () => {
        this.handleModalQtyChange('plus');
      });
    }

    // Add button: close modal (quantity already synced)
    const addBtn = document.getElementById('bundle-modal-add-btn');
    if (addBtn) {
      addBtn.addEventListener('click', () => {
        this.closeProductModal();
      });
    }

    // Close modal on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.style.display !== 'none') {
        this.closeProductModal();
      }
    });
  }

  /**
   * Handle quantity change from modal qty picker
   * @param {string} action - 'plus' or 'minus'
   */
  handleModalQtyChange(action) {
    if (!this.currentModalVariantId) return;

    const qtyInput = document.getElementById('bundle-modal-qty-input');
    let currentQty = this.quantities.get(this.currentModalVariantId) || 0;

    if (action === 'plus') {
      currentQty++;
    } else if (action === 'minus' && currentQty > 0) {
      currentQty--;
    }

    // Update quantities map
    if (currentQty <= 0) {
      this.quantities.delete(this.currentModalVariantId);
    } else {
      this.quantities.set(this.currentModalVariantId, currentQty);
    }

    // Update modal input display
    if (qtyInput) {
      qtyInput.value = currentQty;
    }

    // Sync with the product card in the bundle builder
    this.syncCardQuantityFromModal(this.currentModalVariantId, currentQty);

    // Update step completion
    const card = this.querySelector(`[data-variant-id="${this.currentModalVariantId}"]`);
    if (card) {
      this.updateStepCompletionForVertical(card);
    }

    // Update button price and dispatch event
    this.updateButtonPrice();
    this.dispatchQuantityChangeEvent();
  }

  /**
   * Sync card quantity controls with modal quantity
   * @param {string} variantId
   * @param {number} quantity
   */
  syncCardQuantityFromModal(variantId, quantity) {
    const card = this.querySelector(`[data-variant-id="${variantId}"]`);
    if (!card) return;

    const addButton = card.querySelector('.bundle-product-card__add-btn');
    const quantityControls = card.querySelector(`.bundle-product-card__quantity-controls[data-variant-id="${variantId}"]`);
    const quantityInput = quantityControls?.querySelector('.bundle-product-card__quantity-input');

    if (!quantityControls || !addButton) return;

    if (quantity > 0) {
      // Show quantity controls, hide add button
      addButton.style.display = 'none';
      quantityControls.style.display = 'flex';
      if (quantityInput) quantityInput.value = quantity;
    } else {
      // Show add button, hide quantity controls
      addButton.style.display = 'inline-flex';
      quantityControls.style.display = 'none';
      if (quantityInput) quantityInput.value = '0';
    }
  }

  /**
   * Open product modal and fetch quick view content
   * @param {string} productHandle - Product handle for fetching
   * @param {string} variantId - Variant ID for qty picker
   */
  async openProductModal(productHandle, variantId) {
    const modal = document.getElementById('bundle-product-modal');
    if (!modal) return;

    const loadingEl = document.getElementById('bundle-modal-loading');
    const bodyEl = document.getElementById('bundle-modal-body');
    const footerEl = document.getElementById('bundle-modal-footer');
    const qtyInput = document.getElementById('bundle-modal-qty-input');

    // Show modal with loading state
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Initialize qty picker with current quantity
    if (qtyInput && variantId) {
      const currentQty = this.quantities.get(variantId) || 0;
      qtyInput.value = currentQty;
    }

    if (loadingEl) loadingEl.style.display = 'flex';
    if (bodyEl) bodyEl.style.display = 'none';
    if (footerEl) footerEl.style.display = 'none';

    try {
      // Fetch product quick view content
      const response = await fetch(
        `${Shopify.routes.root}products/${productHandle}?view=bundle-quick-view`,
        { method: 'GET' }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const html = await response.text();

      // Parse the response and extract quick view content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const quickViewContent = doc.querySelector('[bundle-quick-view-content]');

      if (quickViewContent && bodyEl) {
        // Inject the content
        bodyEl.innerHTML = quickViewContent.outerHTML;

        // Initialize any sliders/carousels in the injected content
        this.initializeQuickViewComponents(bodyEl);

        // Hide loading, show content
        if (loadingEl) loadingEl.style.display = 'none';
        bodyEl.style.display = 'block';
        if (footerEl) footerEl.style.display = 'flex';
      } else {
        throw new Error('Quick view content not found in response');
      }
    } catch (error) {
      console.error('Error loading product quick view:', error);

      // Show error state
      if (bodyEl) {
        bodyEl.innerHTML = `
          <div class="bundle-quick-view__error">
            <p>Impossible de charger les informations du produit.</p>
            <button type="button" class="bundle-quick-view__error-close" onclick="this.closest('bundle-builder').closeProductModal()">
              Fermer
            </button>
          </div>
        `;
        bodyEl.style.display = 'block';
      }
      if (loadingEl) loadingEl.style.display = 'none';
    }
  }

  /**
   * Initialize components in quick view content (sliders, etc.)
   * @param {HTMLElement} container - Container with quick view content
   */
  initializeQuickViewComponents(container) {
    // Re-initialize Swiper carousels if present
    const carousels = container.querySelectorAll('product-carousel');
    carousels.forEach((carousel) => {
      // Trigger connectedCallback by re-appending
      if (carousel.isConnected && typeof carousel.initSlider === 'function') {
        carousel.initSlider();
      }
    });

    // Initialize truncate-text components
    const truncateComponents = container.querySelectorAll('truncate-text');
    truncateComponents.forEach((component) => {
      if (typeof component.connectedCallback === 'function') {
        component.connectedCallback();
      }
    });
  }

  /**
   * Close product modal
   */
  closeProductModal() {
    const modal = document.getElementById('bundle-product-modal');
    if (!modal) return;

    const bodyEl = document.getElementById('bundle-modal-body');

    // Hide modal
    modal.style.display = 'none';
    document.body.style.overflow = '';

    // Clear content after animation
    setTimeout(() => {
      if (bodyEl) bodyEl.innerHTML = '';
    }, 300);
  }

  /**
   * Initialize scroll indicators for vertical layouts
   */
  initializeScrollIndicators() {
    const scrollWrappers = this.querySelectorAll('.bundle-builder__scroll-wrapper');

    scrollWrappers.forEach((scrollWrapper) => {
      // Check if content needs scrolling
      const checkScrollNeeded = () => {
        const hasScroll = scrollWrapper.scrollHeight > scrollWrapper.clientHeight;
        if (!hasScroll) {
          scrollWrapper.classList.add('no-scroll');
        } else {
          scrollWrapper.classList.remove('no-scroll');
        }
      };

      // Handle scroll state (hide indicator at bottom)
      const handleScroll = () => {
        const scrollTop = scrollWrapper.scrollTop;
        const scrollHeight = scrollWrapper.scrollHeight;
        const clientHeight = scrollWrapper.clientHeight;

        // Consider at bottom if within 10px of bottom
        const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

        if (isAtBottom) {
          scrollWrapper.classList.add('scrolled-to-bottom');
        } else {
          scrollWrapper.classList.remove('scrolled-to-bottom');
        }
      };

      // Check on load
      checkScrollNeeded();
      handleScroll();

      // Listen to scroll events
      scrollWrapper.addEventListener('scroll', handleScroll);

      // Re-check after short delay (for images loading)
      setTimeout(() => {
        checkScrollNeeded();
        handleScroll();
      }, 500);

      // Re-check on window resize
      window.addEventListener('resize', () => {
        checkScrollNeeded();
        handleScroll();
      });
    });
  }
}

// Register custom element
customElements.define('bundle-builder', BundleBuilderElement);
